import * as ort from 'onnxruntime-web'
import {
  projectToNormalized,
  snapToRay,
  mat4,
  quat,
  type CameraModel,
  type FaceFrame,
  type Vec3,
} from '@ar-suite/core'

/**
 * Model contracts:
 *  - 'earnet' (default): Harshit's iBUG-55 heatmap model (ai/ar-earrings, v9) — 192px input
 *    normalized to [-1,1], heatmaps [1,55,48,48], lobe = mean of points 16/17/18, subject-LEFT
 *    crops mirrored, crop = faceWidth×0.55 centred slightly above the lobe anchor.
 *  - 'v0': the in-repo synthetic-trained 5-keypoint model (ml/ear-tracker) — 96px input in [0,1],
 *    heatmaps [1,5,24,24], subject-RIGHT crops mirrored.
 */
export type EarModelContract = 'earnet' | 'v0'

export interface EarTrackerOptions {
  /** URL of the ear keypoint ONNX model. */
  modelPath: string
  contract: EarModelContract
  /** Detected-vs-expected sanity clamp as a fraction of the crop size (v0 contract only). */
  maxDeviation: number
}

export const EAR_TRACKER_DEFAULTS: EarTrackerOptions = {
  modelPath: '/models/earnet.onnx',
  contract: 'earnet',
  maxDeviation: 0.35,
}

export const EAR_KP_NAMES = ['tragus', 'lobe_attach', 'lobe_bottom', 'ear_center', 'helix_top'] as const
export type EarKeypointName = (typeof EAR_KP_NAMES)[number]

export interface EarObservation {
  /** Normalized image coords per keypoint. */
  points: Record<EarKeypointName, { x: number; y: number; visibility: number }>
  /** Overall confidence (mean lobe-region visibility, zeroed if the sanity clamp trips). */
  confidence: number
  /** ROI used, normalized [x, y, size] — for debug overlays. */
  roi: [number, number, number]
}

export interface EarTrackerResult {
  left: EarObservation | null
  right: EarObservation | null
  inferenceMs: number
}

const V0_IN = 96
const EARNET_IN = 192
const EARNET_HM = 48
const EARNET_POINTS = 55
/** iBUG-55 lobe indices (17 = lobe tip); anchor = their mean, matching the validated web app. */
const EARNET_LOBE_IDS = [16, 17, 18]
const EARNET_MIN_CONFIDENCE = 0.25
/** Canonical (MediaPipe face space, cm) ear ROI reference points. */
const EAR_TOP: Vec3 = [7.66, 4.4, -2.4]
const EAR_BOTTOM: Vec3 = [7.4, -2.8, -3.5]
const EAR_CENTER: Vec3 = [7.7, 1.0, -2.9]
const EAR_EDGE: Vec3 = [7.66, 0.67, -2.44]
const EDGE_TO_LOBE: Vec3 = [-0.31, -2.57, -1.16]

/**
 * Our own ear keypoint detector (synthetic-trained, see ml/ear-tracker).
 * Crops an ear ROI predicted by the face head matrix, runs a 5-keypoint CNN
 * (~0.4M params) per ear via onnxruntime-web, and maps results back to
 * normalized image coordinates. Right ears are mirrored to the canonical
 * left-ear appearance before inference.
 */
export class EarTracker {
  readonly options: EarTrackerOptions
  private session: ort.InferenceSession | null = null
  private canvas: OffscreenCanvas | HTMLCanvasElement
  private ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D
  private input: Float32Array

  private readonly IN: number

  constructor(options: Partial<EarTrackerOptions> = {}) {
    this.options = { ...EAR_TRACKER_DEFAULTS, ...options }
    this.IN = this.options.contract === 'earnet' ? EARNET_IN : V0_IN
    this.canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(this.IN, this.IN) : document.createElement('canvas')
    this.canvas.width = this.IN
    this.canvas.height = this.IN
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('2D context unavailable')
    this.ctx = ctx as CanvasRenderingContext2D
    this.input = new Float32Array(3 * this.IN * this.IN)
  }

  get ready(): boolean {
    return this.session !== null
  }

  async init(): Promise<void> {
    if (this.session) return
    // Bundled ort build: the JS loader is embedded and fetches its .wasm relative to the
    // ort module URL (works in dev; production must place ort-wasm-simd-threaded.wasm next
    // to the built JS chunk — see scripts/deploy-demo.sh). Overriding env.wasm.wasmPaths
    // breaks this build ("K is not a function"), so we deliberately leave it untouched.
    this.session = await ort.InferenceSession.create(this.options.modelPath, {
      executionProviders: ['wasm'],
    })
  }

  /** Detect both ears for the current face frame. */
  async detect(video: HTMLVideoElement, face: FaceFrame, cam: CameraModel): Promise<EarTrackerResult> {
    if (!this.session) throw new Error('EarTracker.init() has not completed')
    const t0 = performance.now()
    const left = await this.detectSide(video, face, cam, 'left')
    const right = await this.detectSide(video, face, cam, 'right')
    return { left, right, inferenceMs: performance.now() - t0 }
  }

  private roiFor(face: FaceFrame, cam: CameraModel, side: 'left' | 'right'): [number, number, number] | null {
    const sign = side === 'left' ? 1 : -1
    const p: Vec3 = [0, 0, 0]
    const uv: [number, number] = [0, 0]
    const project = (c: Vec3): [number, number] | null => {
      mat4.transformPoint(p, face.headMatrix, [sign * c[0]!, c[1]!, c[2]!])
      return projectToNormalized(uv, cam, p) ? [uv[0], uv[1]] : null
    }
    if (this.options.contract === 'earnet') return this.roiForEarnet(face, cam, side)
    const top = project(EAR_TOP)
    if (!top) return null
    const ty = top[1]
    const bot = project(EAR_BOTTOM)!
    const ctr: [number, number] = project(EAR_CENTER)!
    // NOTE: detect→track ROI re-centring was tried and removed — the v0 model biases toward the
    // crop centre when the ear is off-distribution, so feeding detections back into the ROI drifts.
    // Reintroduce once the model has a correctness-aware confidence (v1: negative-sample training).
    // ear span in normalized-height units → square ROI 2.3× the span
    const span = Math.hypot((bot[0] - top[0]) * cam.aspect, bot[1] - ty)
    const size = span * 2.3
    // ROI x is normalized to WIDTH; a square of `size` height units is size/aspect width units
    return [ctr[0] - size / (2 * cam.aspect), ctr[1] - size / 2, size]
  }

  /**
   * earnet crop recipe (must match the validated web app): square of faceWidth×0.55 centred
   * at the lobe anchor and shifted up by 0.18×size. Lobe anchor = observed mesh-edge landmark
   * (454/234) ray at head-matrix depth + canonical edge→lobe offset — the solver's fallback point.
   */
  private roiForEarnet(face: FaceFrame, cam: CameraModel, side: 'left' | 'right'): [number, number, number] | null {
    const sign = side === 'left' ? 1 : -1
    const lmIndex = side === 'left' ? 454 : 234
    const p: Vec3 = [0, 0, 0]
    const uv: [number, number] = [0, 0]
    // face width in normalized-height units from the two mesh-edge landmarks
    const lx = face.landmarks[454 * 3]!, ly = face.landmarks[454 * 3 + 1]!
    const rx = face.landmarks[234 * 3]!, ry = face.landmarks[234 * 3 + 1]!
    const faceWidth = Math.hypot((lx - rx) * cam.aspect, ly - ry)
    if (!(faceWidth > 0.01)) return null
    // lobe anchor: edge landmark ray at canonical-edge depth, plus rotated edge→lobe offset
    mat4.transformPoint(p, face.headMatrix, [sign * EAR_EDGE[0]!, EAR_EDGE[1]!, EAR_EDGE[2]!])
    snapToRay(p, cam, p, face.landmarks[lmIndex * 3]!, face.landmarks[lmIndex * 3 + 1]!)
    const off: Vec3 = [sign * EDGE_TO_LOBE[0]!, EDGE_TO_LOBE[1]!, EDGE_TO_LOBE[2]!]
    const q: [number, number, number, number] = [0, 0, 0, 1]
    quat.fromMat4(q, face.headMatrix)
    quat.rotateVec3(off, q, off)
    p[0] += off[0]
    p[1] += off[1]
    p[2] += off[2]
    if (!projectToNormalized(uv, cam, p)) return null
    const size = faceWidth * 0.55
    const cy = uv[1] - size * 0.18
    return [uv[0] - size / (2 * cam.aspect), cy - size / 2, size]
  }

  private async detectSide(
    video: HTMLVideoElement,
    face: FaceFrame,
    cam: CameraModel,
    side: 'left' | 'right',
  ): Promise<EarObservation | null> {
    const roi = this.roiFor(face, cam, side)
    if (!roi) return null
    const [rx, ry, size] = roi
    const W = video.videoWidth, H = video.videoHeight
    // ROI is [x, y] normalized to width/height with size in height units → a square of s pixels
    const s = size * H
    const sx = rx * W
    const sy = ry * H
    if (s < 8) return null
    const IN = this.IN
    const earnet = this.options.contract === 'earnet'
    // earnet's canonical orientation mirrors subject-LEFT crops; v0 mirrors RIGHT
    const mirror = earnet ? side === 'left' : side === 'right'
    this.ctx.save()
    if (mirror) {
      this.ctx.translate(IN, 0)
      this.ctx.scale(-1, 1)
    }
    this.ctx.drawImage(video, sx, sy, s, s, 0, 0, IN, IN)
    this.ctx.restore()
    const img = this.ctx.getImageData(0, 0, IN, IN).data
    const n = IN * IN
    for (let i = 0; i < n; i++) {
      if (earnet) {
        this.input[i] = (img[i * 4]! / 255 - 0.5) / 0.5
        this.input[n + i] = (img[i * 4 + 1]! / 255 - 0.5) / 0.5
        this.input[2 * n + i] = (img[i * 4 + 2]! / 255 - 0.5) / 0.5
      } else {
        this.input[i] = img[i * 4]! / 255
        this.input[n + i] = img[i * 4 + 1]! / 255
        this.input[2 * n + i] = img[i * 4 + 2]! / 255
      }
    }
    const out = await this.session!.run({ image: new ort.Tensor('float32', this.input, [1, 3, IN, IN]) })
    if (earnet) return this.decodeEarnet(out, mirror, sx, sy, s, W, H, roi)
    const hm = out.heatmaps!
    const vis = out.visibility!.data as Float32Array
    const HMS = hm.dims[3]!
    const hmData = hm.data as Float32Array

    const points = {} as EarObservation['points']
    let dev = 0
    for (let k = 0; k < EAR_KP_NAMES.length; k++) {
      // decode: argmax cell + 3x3 soft window (robust to off-centre ears)
      const plane = k * HMS * HMS
      let bi = 0
      for (let i = 1; i < HMS * HMS; i++) if (hmData[plane + i]! > hmData[plane + bi]!) bi = i
      const iy = (bi / HMS) | 0
      const ix = bi % HMS
      let wsum = 0, xsum = 0, ysum = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const yy = iy + dy, xx = ix + dx
          if (yy < 0 || yy >= HMS || xx < 0 || xx >= HMS) continue
          const w = Math.exp(hmData[plane + yy * HMS + xx]! * 4)
          wsum += w
          xsum += w * xx
          ysum += w * yy
        }
      }
      let cx = (xsum / wsum + 0.5) / HMS
      const cy = (ysum / wsum + 0.5) / HMS
      if (side === 'right') cx = 1 - cx
      points[EAR_KP_NAMES[k]!] = {
        x: (sx + cx * s) / W,
        y: (sy + cy * s) / H,
        visibility: vis[k]!,
      }
      dev = Math.max(dev, Math.abs(cx - 0.5), Math.abs(cy - 0.5))
    }
    // lobe_attach and ear_center are the two reliably-predicted keypoints (lobe_bottom is noisy)
    const lobeVis = points.lobe_attach.visibility * 0.6 + points.ear_center.visibility * 0.4
    const confidence = dev > 0.5 + this.options.maxDeviation ? 0 : lobeVis
    return { points, confidence, roi }
  }

  /** earnet: [1,55,48,48] heatmaps → argmax + quarter-pixel shift; lobe anchor = mean of 16/17/18. */
  private decodeEarnet(
    out: Record<string, ort.Tensor>,
    mirror: boolean,
    sx: number,
    sy: number,
    s: number,
    W: number,
    H: number,
    roi: [number, number, number],
  ): EarObservation | null {
    const hms = out.heatmaps!.data as Float32Array
    const HM = EARNET_HM
    const decode = (k: number): { x: number; y: number; conf: number } => {
      const off = k * HM * HM
      let best = -Infinity, bx = 0, by = 0
      for (let i = 0; i < HM * HM; i++) {
        const v = hms[off + i]!
        if (v > best) {
          best = v
          bx = i % HM
          by = (i / HM) | 0
        }
      }
      let fx = bx, fy = by
      const at = (x: number, y: number): number => hms[off + y * HM + x]!
      if (bx > 0 && bx < HM - 1) fx += 0.25 * Math.sign(at(bx + 1, by) - at(bx - 1, by))
      if (by > 0 && by < HM - 1) fy += 0.25 * Math.sign(at(bx, by + 1) - at(bx, by - 1))
      return { x: fx / HM, y: fy / HM, conf: best }
    }
    let lx = 0, ly = 0
    let minConf = Infinity
    for (const k of EARNET_LOBE_IDS) {
      const pt = decode(k)
      lx += pt.x / EARNET_LOBE_IDS.length
      ly += pt.y / EARNET_LOBE_IDS.length
      minConf = Math.min(minConf, pt.conf)
    }
    const tip = decode(17)
    const unmirror = (x: number): number => (mirror ? 1 - x : x)
    const toFrame = (cx: number, cy: number): { x: number; y: number } => ({ x: (sx + unmirror(cx) * s) / W, y: (sy + cy * s) / H })
    const lobe = toFrame(lx, ly)
    const bottom = toFrame(tip.x, tip.y)
    // accepted detections get full solver weight (mirrors the validated app's binary 0.25 gate)
    const confidence = minConf < EARNET_MIN_CONFIDENCE ? 0 : 0.8 + 0.2 * Math.min(1, (minConf - EARNET_MIN_CONFIDENCE) / 0.4)
    const vis = Math.max(0, Math.min(1, minConf))
    const points = {
      tragus: { ...lobe, visibility: vis },
      lobe_attach: { ...lobe, visibility: vis },
      lobe_bottom: { ...bottom, visibility: vis },
      ear_center: { ...lobe, visibility: vis },
      helix_top: { ...lobe, visibility: vis },
    }
    return { points, confidence, roi }
  }

  close(): void {
    void this.session?.release()
    this.session = null
  }
}
